import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { MediumCircleContainer } from "../../components/CircleContainer/styles";
import { KeepoLoader } from "../../components/KeepoLoader";
import { ScreenHeader } from "../../components/SubHeader";
import CheckIcon from "../../icons/Check";
import ChevronRightIcon from "../../icons/ChevronRight";
import { GlobeIcon } from "../../icons/Globe";
import { getMyDomains, OwnedDomain } from "../../services/domains";
import { formatDate as formatLocaleDate } from "../../services/i18ln/format";
import { OpenInNewIcon } from "../Sites/components/CardSite";
import { Actions, Info, TitleRow } from "../Sites/components/CardSite/styles";
import {
  Banner,
  Container,
  DomainList,
  DomainStatus,
  Note,
  Section,
  Skeleton,
  SkeletonList,
  SmallButton,
} from "./styles";

// Right after buying: how often (ms) and how many times the list is checked for the domain to be registered
const POLL_MS = 4000;
const MAX_POLLS = 30;

/**
 * A domain's icon: the one of the Keepo it opens (its favicon, else its logo; already in our bucket, so nothing is
 * asked of the domain itself), the globe when there's none or it doesn't load.
 */
const DomainIcon = ({ icon }: { icon?: string | null }) => {
  const [failed, setFailed] = useState(false);
  return (
    <span className="thumb">
      {icon && !failed ? (
        <img src={icon} alt="" loading="lazy" onError={() => setFailed(true)} />
      ) : (
        <GlobeIcon size={22} />
      )}
    </span>
  );
};

/**
 * My domains: the ones bought at Keepo, each opening its management (DNS, email forwarding, security, its Keepo).
 * Back from the checkout, the domain just paid shows as being registered until it's active.
 */
const MyDomainsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const prefix = "pages.domains";
  const [domains, setDomains] = useState<OwnedDomain[]>();
  const [hasLoadError, setLoadError] = useState(false);
  const location = useLocation();
  // Kept from the arrival (then dropped from the history state: a reload doesn't bring the banner back)
  const [justBought] = useState(() => (location.state as { justBought?: string } | null)?.justBought);
  const bought = justBought ? domains?.find(item => item.domain === justBought) : undefined;
  const isRegistered = !!bought && bought.status !== "pending";

  const load = () => {
    setLoadError(false);
    getMyDomains()
      .then(setDomains)
      .catch(() => setLoadError(true));
  };

  useEffect(load, []);

  useEffect(() => {
    if (justBought) {
      navigate(".", { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!justBought || isRegistered) {
      return;
    }
    let polls = 0;
    const timer = window.setInterval(() => {
      // Registration that never shows (refused, refunded) isn't checked forever
      if (++polls > MAX_POLLS) {
        window.clearInterval(timer);
        return;
      }
      getMyDomains()
        .then(setDomains)
        .catch(() => undefined);
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [justBought, isRegistered]);

  const formatDate = (date: string) => formatLocaleDate(date, i18n.language);

  // Under the domain, like a Keepo's "● Ativo" on /sites: being registered (or its Pix awaited) pulses
  const statusOf = (domain: OwnedDomain) => (
    <DomainStatus $status={domain.status}>
      {domain.status === "pending"
        ? t(`${prefix}.mine.${domain.paymentPending ? "waitingPayment" : "registering"}`)
        : t(`${prefix}.status.${domain.status}`)}
    </DomainStatus>
  );

  const openDomain = (domain: OwnedDomain) => navigate(`/domains/${encodeURIComponent(domain.domain)}`);

  if (hasLoadError && !domains) {
    return (
      <Container>
        <ScreenHeader title={t(`${prefix}.mine.title`)} />
        <Section>
          <Note role="alert">{t(`${prefix}.mine.loadError`)}</Note>
          <SmallButton type="button" $primary style={{ alignSelf: "flex-start" }} onClick={load}>
            {t(`${prefix}.mine.retry`)}
          </SmallButton>
        </Section>
      </Container>
    );
  }

  // Loading: the page's shape (title, a few rows) pulsing, as on /sites, not the title over an empty list
  if (!domains) {
    return (
      <Container aria-busy="true">
        <Section className="title">
          <Skeleton className="title" />
        </Section>
        <Section>
          <SkeletonList>
            {[0, 1, 2].map(row => (
              <li key={row}>
                <Skeleton className="icon" />
                <span className="text">
                  <Skeleton className="name" />
                  <Skeleton className="meta" />
                  <Skeleton className="status" />
                </span>
              </li>
            ))}
          </SkeletonList>
        </Section>
      </Container>
    );
  }

  return (
    <Container>
      <ScreenHeader title={t(`${prefix}.mine.title`)} />

      {justBought && (
        <Section>
          {/* The domain just bought: being registered (Keepo's loading), then theirs (a check, and Open) */}
          <Banner $done={isRegistered} role="status" aria-busy={!isRegistered}>
            <span className="state" aria-hidden="true">
              {isRegistered ? (
                <CheckIcon width={14} height={14} color="currentColor" />
              ) : (
                <KeepoLoader size={14} color="var(--color-neutral-white)" />
              )}
            </span>
            <span className="text">
              <span className="label">
                {t(`${prefix}.bought.${isRegistered ? "registered" : "registering"}.label`)}
              </span>
              <b className="domain">{justBought}</b>
              <span className="detail">
                {t(`${prefix}.bought.${isRegistered ? "registered" : "registering"}.text`)}
              </span>
            </span>
            {isRegistered && (
              <SmallButton
                type="button"
                $primary
                onClick={() => navigate(`/domains/${encodeURIComponent(justBought)}`)}>
                {t(`${prefix}.bought.registered.open`)}
              </SmallButton>
            )}
          </Banner>
        </Section>
      )}

      {domains && (
        <Section>
          {domains.length ? (
            <DomainList>
              {domains.map(domain => (
                <li key={domain.domain}>
                  {/* The Keepo card's layout (/sites): a div, since the "open" button sits inside it */}
                  <div
                    className="card"
                    role="button"
                    tabIndex={0}
                    onClick={() => openDomain(domain)}
                    onKeyDown={event => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openDomain(domain);
                      }
                    }}>
                    <DomainIcon icon={domain.keepo?.icon} />
                    <Info>
                      <TitleRow>
                        <h4>{domain.domain}</h4>
                      </TitleRow>
                      <p className="url">
                        {domain.expiresAt &&
                          `${t(`${prefix}.mine.validUntil`, { date: formatDate(domain.expiresAt) })} · `}
                        {/* The Keepo's address, in white */}
                        {domain.keepo ? (
                          <Trans
                            i18nKey={`${prefix}.mine.connected`}
                            values={{ keepo: `${domain.keepo.url}.keepo.bio` }}
                            components={{ url: <span className="keepo-url" /> }}
                          />
                        ) : (
                          t(`${prefix}.mine.notConnected`)
                        )}
                      </p>
                      {statusOf(domain)}
                    </Info>
                    <Actions>
                      {/* Live once registered (an expired one doesn't open anymore) */}
                      {(domain.status === "active" || domain.status === "expiring") && (
                        <MediumCircleContainer
                          className="launch"
                          role="button"
                          tabIndex={0}
                          aria-label={t(`${prefix}.mine.open`)}
                          title={t(`${prefix}.mine.open`)}
                          $color="var(--color-neutral-base)"
                          cursor="pointer"
                          onClick={(event: React.MouseEvent) => {
                            event.stopPropagation();
                            window.open(`https://${domain.domain}`, "_blank", "noopener,noreferrer");
                          }}
                          onKeyDown={(event: React.KeyboardEvent) => {
                            if (event.key === "Enter") {
                              event.stopPropagation();
                              window.open(`https://${domain.domain}`, "_blank", "noopener,noreferrer");
                            }
                          }}>
                          <OpenInNewIcon color="var(--color-neutral-white)" />
                        </MediumCircleContainer>
                      )}
                      <span className="chevron">
                        <ChevronRightIcon width={14} height={14} color="currentColor" />
                      </span>
                    </Actions>
                  </div>
                </li>
              ))}
            </DomainList>
          ) : (
            <>
              <Note>{t(`${prefix}.mine.empty`)}</Note>
              <SmallButton
                type="button"
                $primary
                style={{ alignSelf: "flex-start" }}
                onClick={() => navigate("/domains")}>
                {t(`${prefix}.mine.search`)}
              </SmallButton>
            </>
          )}
        </Section>
      )}
    </Container>
  );
};

export default MyDomainsPage;
